import { useEffect, useRef, useCallback, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';

/**
 * Custom hook for handling virtual scrolling of large lists
 * Measures item heights and manages visible range to optimize performance
 *
 * @param {Object} options - Configuration options
 * @param {number} options.overscan - Number of items to render outside visible area
 * @param {number} options.estimatedItemHeight - Estimated height for initial calculations
 * @param {HTMLElement} options.containerRef - Reference to scrollable container
 * @param {number} options.containerPaddingVertical - Total vertical padding of inner container
 * @param {Array} options.data - Data array to detect pagination changes
 * @param {number} options.paginationPage - Current page number for pagination tracking
 * @returns {Object} Virtual scrolling state and helpers
 */
export const useVirtualScrolling = ({
  overscan = 5,
  estimatedItemHeight = isMobileOnly ? 220 : 150,
  containerRef,
  itemCount = 0,
  dynamicHeightEnabled = true,
  containerPaddingVertical = 0,
  data = [],
  paginationPage = 1,
} = {}) => {
  const [visibleRange, setVisibleRange] = useState({
    startIndex: 0,
    endIndex: Math.min(20, itemCount),
  });

  const itemHeights = useRef(new Map());
  const resizeObserver = useRef(null);
  const contentHeightMap = useRef(new Map());
  const isScrolling = useRef(false);
  const scrollTimeout = useRef(null);
  const prevDataRef = useRef(data);
  const prevPageRef = useRef(paginationPage);

  /**
   * Get total height of all items using cached heights
   * Includes intelligent buffer for unmeasured items and spacing variations
   */
  const getTotalHeight = useCallback(() => {
    let total = 0;
    let unmeasuredCount = 0;

    for (let i = 0; i < itemCount; i++) {
      const height = itemHeights.current.get(i);
      if (height) {
        total += height;
      } else {
        total += estimatedItemHeight;
        unmeasuredCount++;
      }
    }

    // Add buffer: 10% for unmeasured items + base buffer for spacing/margins
    const unmeasuredBuffer = unmeasuredCount > 0 ? unmeasuredCount * estimatedItemHeight * 0.1 : 0;
    const spacingBuffer = itemCount * 2; // 2px per item for margins/spacing variations

    // No mobileMarginBuffer needed - ResizeObserver already includes margin in measured heights
    return total + containerPaddingVertical + unmeasuredBuffer + spacingBuffer;
  }, [itemCount, estimatedItemHeight, containerPaddingVertical]);

  // Get offset position of item at index
  const getItemOffset = useCallback(
    (index) => {
      let offset = 0;
      for (let i = 0; i < index; i++) {
        offset += itemHeights.current.get(i) || estimatedItemHeight;
      }
      return offset;
    },
    [estimatedItemHeight],
  );

  // Calculate which items should be visible based on scroll position
  const calculateVisibleRange = useCallback(() => {
    if (!containerRef?.current || !dynamicHeightEnabled) {
      return;
    }

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const scrollBottom = scrollTop + clientHeight;

    let startIndex = 0;
    let offset = 0;

    // Find start index - iterate until offset exceeds scrollTop
    for (let i = 0; i < itemCount; i++) {
      const itemHeight = itemHeights.current.get(i) || estimatedItemHeight;

      if (offset + itemHeight > scrollTop) {
        startIndex = Math.max(0, i - overscan);
        break;
      }

      offset += itemHeight;
    }

    // Calculate end index - start from visible offset, not from startIndex
    let endIndex = startIndex;
    let currentOffset = getItemOffset(startIndex);

    for (let i = startIndex; i < itemCount; i++) {
      const itemHeight = itemHeights.current.get(i) || estimatedItemHeight;

      if (currentOffset > scrollBottom) {
        break;
      }

      endIndex = i;
      currentOffset += itemHeight;
    }

    endIndex = Math.min(itemCount - 1, endIndex + overscan);
    setVisibleRange({ startIndex, endIndex });
  }, [containerRef, itemCount, estimatedItemHeight, overscan, dynamicHeightEnabled, getItemOffset]);

  //  Handle scroll events - debounced
  const handleScroll = useCallback(() => {
    isScrolling.current = true;

    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    calculateVisibleRange();

    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
    }, 150);
  }, [calculateVisibleRange]);

  // Get ref callback for measuring item height
  const getItemRef = useCallback(
    (index) => (element) => {
      if (element && dynamicHeightEnabled) {
        // Set data-index attribute for ResizeObserver
        element.setAttribute('data-index', index);

        // Store reference for future measurement
        if (!itemHeights.current.has(index)) {
          const height = element.offsetHeight;
          if (height > 0) {
            itemHeights.current.set(index, height);
            // Recalculate range immediately when new height is measured
            requestAnimationFrame(() => {
              calculateVisibleRange();
            });
          }
        }
      }
    },
    [dynamicHeightEnabled, calculateVisibleRange],
  );

  // Handle container resize
  const handleContainerResize = useCallback(() => {
    calculateVisibleRange();
  }, [calculateVisibleRange]);

  // Initialize ResizeObserver for item height tracking
  useEffect(() => {
    if (!dynamicHeightEnabled) return;

    const observerCallback = (entries) => {
      let heightsChanged = false;

      entries.forEach((entry) => {
        const element = entry.target;
        const index = parseInt(element.getAttribute('data-index'), 10);

        if (!isNaN(index)) {
          // Use offsetHeight which excludes margin-bottom
          // Mobile items have margin-bottom: 8px (VirtualListRenderer:46), desktop items don't
          // offsetHeight excludes margin, so add it for mobile to get true spacing
          const newHeight = element.offsetHeight;
          const marginCompensation = isMobileOnly ? 8 : 0;
          const totalHeight = newHeight + marginCompensation;

          const cachedHeight = itemHeights.current.get(index);

          // Only update if height actually changed by more than 1px
          if (!cachedHeight || Math.abs(cachedHeight - totalHeight) > 1) {
            itemHeights.current.set(index, totalHeight);
            heightsChanged = true;
          }
        }
      });

      // Recalculate visible range if any heights changed
      if (heightsChanged) {
        requestAnimationFrame(() => {
          calculateVisibleRange();
        });
      }
    };

    resizeObserver.current = new ResizeObserver(observerCallback);
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
        resizeObserver.current = null;
      }
    };
  }, [dynamicHeightEnabled, calculateVisibleRange]);

  // Setup scroll listener on container
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !dynamicHeightEnabled) {
      return;
    }

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Setup ResizeObserver on container for size changes
    const containerResizeObserver = new ResizeObserver(handleContainerResize);
    containerResizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      containerResizeObserver.disconnect();
    };
  }, [containerRef, dynamicHeightEnabled, handleScroll, handleContainerResize]);

  // Clear itemHeights cache when data array reference changes (pagination)
  useEffect(() => {
    if (prevDataRef.current !== data) {
      itemHeights.current.clear();
      setVisibleRange({
        startIndex: 0,
        endIndex: Math.min(20, itemCount),
      });
      prevDataRef.current = data;
    }
  }, [data, itemCount]);

  // Reset height calculations and scroll position when pagination page changes
  useEffect(() => {
    if (prevPageRef.current !== paginationPage) {
      itemHeights.current.clear();
      contentHeightMap.current.clear();

      // Reset scroll position to top
      if (containerRef?.current) {
        containerRef.current.scrollTop = 0;
      }

      // Reset visible range
      setVisibleRange({
        startIndex: 0,
        endIndex: Math.min(20, itemCount),
      });

      // Trigger immediate recalculation
      requestAnimationFrame(() => {
        calculateVisibleRange();
      });

      prevPageRef.current = paginationPage;
    }
  }, [paginationPage, itemCount, calculateVisibleRange]);

  // Observe items in the DOM to track height changes
  const observeItem = useCallback(
    (element) => {
      if (element && resizeObserver.current && dynamicHeightEnabled) {
        resizeObserver.current.observe(element);
      }
    },
    [dynamicHeightEnabled],
  );

  // Stop observing an item
  const unobserveItem = useCallback((element) => {
    if (element && resizeObserver.current) {
      resizeObserver.current.unobserve(element);
    }
  }, []);

  // Get the offset style for positioning visible items
  const getOffsetStyle = useCallback(
    (index) => ({ transform: `translateY(${getItemOffset(index)}px)` }),
    [getItemOffset],
  );

  // Scroll to a specific item index
  const scrollToIndex = useCallback(
    (index, behavior = 'smooth') => {
      if (!containerRef?.current) return;
      const offset = getItemOffset(index);
      containerRef.current.scrollTo({ top: offset, behavior });
    },
    [containerRef, getItemOffset],
  );

  return {
    visibleRange,
    getItemRef,
    observeItem,
    unobserveItem,
    getOffsetStyle,
    getTotalHeight,
    getItemOffset,
    scrollToIndex,
    isScrolling: isScrolling.current,
    itemHeights: itemHeights.current, // Expose itemHeights map
  };
};

export default useVirtualScrolling;
